import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  User,
  Users,
  Sparkles,
  Package,
  MailIcon,
  Shield,
  Megaphone,
  Zap,
  Bell,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Info,
  Camera,
  Scissors,
  Plus,
  Building,
  Mail,
  Key,
  Truck,
  Pencil,
  Trash2,
  Upload,
  FileSpreadsheet,
  Download,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/hooks/use-theme";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { Switch } from "@/components/ui/switch";
import { isStaffEmail } from "@/lib/auth-domain";
import {
  getOrCreateTeamForCurrentCustomer,
  listTeamMembers,
  inviteMember,
  Team,
  TeamMember,
} from "@/lib/teams";
import { supabase } from "@/integrations/supabase/client";
import {
  listProducts,
  listShippingMethods,
  listCuttingTiers,
  createProduct,
  updateProduct,
  deleteProduct,
  toggleProductActive,
  createShippingMethod,
  updateShippingMethod,
  deleteShippingMethod,
  toggleShippingActive,
  createCuttingTier,
  updateCuttingTier,
  toggleCuttingTierActive,
  Product,
  ShippingMethod,
  CuttingTier,
} from "@/lib/products";
import { getAppSetting, updateAppSetting } from "@/lib/app-settings";
import { getIntegration, updateIntegration } from "@/lib/integrations";
import { useGangSheetBuilderScript } from "@/hooks/useGangSheetBuilderScript";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { fetchNotificationPreferences, upsertNotificationPreferences } from "@/lib/notifications";
import type { NotificationPreference } from "@/lib/types";
import { getSystemHealth, type SystemHealth } from "@/lib/system-health";
import { useBrand } from "@/hooks/use-brand";

export const Route = createFileRoute("/app/settings")({ component: SettingsPage });

type Tab =
  | "account"
  | "teams"
  | "system"
  | "products-pricing"
  | "integrations"
  | "import"
  | "about";

function SettingsPage() {
  const { profile } = useAuth();
  const activeBrand = useBrand();
  const { demoMode, setDemoMode } = useDemoMode();
  const [activeTab, setActiveTab] = useState<Tab>("account");

  const { data: systemHealth } = useQuery({
    queryKey: ["system-health"],
    queryFn: getSystemHealth,
    refetchInterval: 60000, // Refetch every minute
  });

  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const isAdmin = profile?.role === "admin";
  const domain = profile?.email?.split("@")[1] ?? "";

  // Account tab state
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreference | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Event preferences state
  const [emailJobCreated, setEmailJobCreated] = useState(true);
  const [emailJobPrintStarted, setEmailJobPrintStarted] = useState(true);
  const [emailJobReady, setEmailJobReady] = useState(true);
  const [emailJobOnHold, setEmailJobOnHold] = useState(true);
  const [inappJobCreated, setInappJobCreated] = useState(true);
  const [inappJobPrintStarted, setInappJobPrintStarted] = useState(true);
  const [inappJobReady, setInappJobReady] = useState(true);
  const [inappJobOnHold, setInappJobOnHold] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      fetchNotificationPreferences(profile.id).then(setNotificationPrefs);
    }
  }, [profile?.id]);

  // Teams tab state
  const [team, setTeam] = useState<Team | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [members, setMembers] = useState<TeamMember[]>([]);

  // Products & Pricing tab state
  const [pricingAnnouncement, setPricingAnnouncement] = useState("");
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddShipping, setShowAddShipping] = useState(false);
  const [showAddCutting, setShowAddCutting] = useState(false);
  const [newProduct, setNewProduct] = useState({
    code: "",
    name: "",
    description: "",
    price_per_m: 0,
    uses_external_builder: false,
    builder_size_id: "",
  });
  const [newShipping, setNewShipping] = useState({
    code: "",
    name: "",
    description: "",
    price: "",
  });
  const [newCutting, setNewCutting] = useState({
    code: "",
    name: "",
    min_items: 0,
    max_items: "",
    price_per_m: "",
    description: "",
  });

  // VAT / tax settings (staff)
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState("20");
  const [vatLabel, setVatLabel] = useState("VAT");
  const [savingVat, setSavingVat] = useState(false);

  // Integrations tab state
  const [dripEnabled, setDripEnabled] = useState(true);
  const [dripShopId, setDripShopId] = useState("");
  const [dripShopApi, setDripShopApi] = useState("");
  const [dripApiBase, setDripApiBase] = useState("https://app.buildagangsheet.com");
  const [savingIntegrations, setSavingIntegrations] = useState(false);

  // Live builder hook so staff can verify it actually loads/opens
  const builderHook = useGangSheetBuilderScript();

  // Builder behaviour settings state
  const [supportedFileTypes, setSupportedFileTypes] = useState("PNG, PDF, AI");
  const [autoResizeDpi, setAutoResizeDpi] = useState(true);
  const [backgroundWarning, setBackgroundWarning] = useState(true);
  const [autoBuild, setAutoBuild] = useState(false);
  const [flippingEnabled, setFlippingEnabled] = useState(true);
  const [canvaEnabled, setCanvaEnabled] = useState(true);
  const [builderNotes, setBuilderNotes] = useState("");

  // Email & SMTP state
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);

  // Email templates state
  const [jobCreatedSubject, setJobCreatedSubject] = useState(
    "Your job {{job_id}} has been created",
  );
  const [jobCreatedBody, setJobCreatedBody] = useState(
    'Hi {{customer_name}},\n\nYour job "{{job_title}}" has been created and is now being processed.\n\nYou can track its progress in FlightDeck.\n\nThanks,\nThe FlightDeck Team',
  );
  const [jobStatusSubject, setJobStatusSubject] = useState("Your job {{job_id}} status update");
  const [jobStatusBody, setJobStatusBody] = useState(
    'Hi {{customer_name}},\n\nYour job "{{job_title}}" has moved to: {{status}}\n\nView details in FlightDeck.\n\nThanks,\nThe FlightDeck Team',
  );
  const [savingTemplates, setSavingTemplates] = useState(false);

  const handleSaveName = async () => {
    setSavingName(true);
    // TODO: Call Supabase to update profile.full_name
    toast.success("Name updated successfully");
    setSavingName(false);
  };

  const handleSaveNotificationPrefs = async () => {
    if (!profile?.id) return;
    setSavingPrefs(true);
    try {
      await upsertNotificationPreferences(profile.id, {
        is_enabled: true,
        category: "job_updates",
        urgency: "normal",
        digest_frequency: "immediate",
        delivery_method: "inapp",
        email_job_created: emailJobCreated,
        email_job_print_started: emailJobPrintStarted,
        email_job_ready: emailJobReady,
        email_job_on_hold: emailJobOnHold,
        inapp_job_created: inappJobCreated,
        inapp_job_print_started: inappJobPrintStarted,
        inapp_job_ready: inappJobReady,
        inapp_job_on_hold: inappJobOnHold,
      });
      toast.success("Notification preferences updated");
    } catch (error) {
      toast.error("Failed to update notification preferences");
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSaveSmtpSettings = async () => {
    setSavingSmtp(true);
    try {
      const { error } = await supabase.from("email_settings").upsert(
        {
          id: 1,
          from_name: fromName || null,
          from_email: fromEmail || null,
          smtp_host: smtpHost || null,
          smtp_port: smtpPort ? parseInt(smtpPort) : null,
          smtp_username: smtpUsername || null,
          smtp_password: smtpPassword || null,
          smtp_use_tls: smtpUseTls,
          smtp_enabled: smtpEnabled,
          updated_at: new Date().toISOString(),
        } as any,
        { onConflict: "id" },
      );

      if (error) throw error;
      toast.success("Email & SMTP settings saved");
    } catch (error) {
      toast.error("Failed to save email settings");
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!profile?.email) {
      toast.error("No email address found");
      return;
    }
    // TODO: Implement actual email sending
    toast.success("Test email sent to " + profile.email);
  };

  const handleSaveEmailTemplates = async () => {
    setSavingTemplates(true);
    try {
      await supabase.from("email_templates").upsert(
        [
          {
            key: "job_created_customer",
            subject: jobCreatedSubject,
            body: jobCreatedBody,
            updated_at: new Date().toISOString(),
          },
          {
            key: "job_status_changed_customer",
            subject: jobStatusSubject,
            body: jobStatusBody,
            updated_at: new Date().toISOString(),
          },
        ],
        { onConflict: "key" },
      );

      toast.success("Email templates saved");
    } catch (error) {
      toast.error("Failed to save email templates");
    } finally {
      setSavingTemplates(false);
    }
  };

  const handleChangeEmail = () => {
    // TODO: Call Supabase auth update email API
    setShowEmailChange(false);
    toast.success(
      "We've sent a confirmation link to your new email. Your sign-in email will update once it's confirmed.",
    );
  };

  const handlePasswordReset = () => {
    // TODO: Call Supabase password reset
    toast.info(
      "Use the 'Forgot password' link on the login page; we'll send a reset email to your account.",
    );
  };

  const handleCreateTeam = async () => {
    if (!profile?.id || !profile?.email) return;
    const newTeam = await getOrCreateTeamForCurrentCustomer(profile.id, profile.email);
    if (newTeam) {
      setTeam(newTeam);
      const teamMembers = await listTeamMembers(newTeam.id);
      setMembers(teamMembers);
      toast.success(`Team created for ${domain}`);
    } else {
      toast.error("Failed to create team");
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail || !team) return;
    try {
      await inviteMember(team.id, inviteEmail, "member");
      toast.success(`Invited ${inviteEmail} to the team`);
      setInviteEmail("");
      // Reload members
      const teamMembers = await listTeamMembers(team.id);
      setMembers(teamMembers);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to invite member");
    }
  };

  // Load team data when Teams tab is active
  useEffect(() => {
    if (activeTab === "teams" && !isStaff && profile?.id && profile?.email) {
      getOrCreateTeamForCurrentCustomer(profile.id, profile.email).then(async (teamData) => {
        if (teamData) {
          setTeam(teamData);
          const teamMembers = await listTeamMembers(teamData.id);
          setMembers(teamMembers);
        }
      });
    }
  }, [activeTab, isStaff, profile?.id, profile?.email]);

  // Load pricing announcement when Products & Pricing tab is active
  useEffect(() => {
    if (activeTab === "products-pricing" && isStaff) {
      getAppSetting("trade_pricing_announcement").then((v) => setPricingAnnouncement(v ?? ""));
      Promise.all([
        getAppSetting("vat_enabled"),
        getAppSetting("vat_rate"),
        getAppSetting("vat_label"),
      ]).then(([enabled, rate, label]) => {
        setVatEnabled(enabled === "true");
        setVatRate(rate ?? "20");
        setVatLabel(label ?? "VAT");
      });
    }
  }, [activeTab, isStaff]);

  // Load integrations when Integrations tab is active
  useEffect(() => {
    if (activeTab === "integrations" && isStaff) {
      Promise.all([
        getIntegration("drip_enabled"),
        getIntegration("drip_shop_id"),
        getIntegration("drip_shop_api"),
        getIntegration("drip_api_base"),
      ]).then(([enabled, shopId, shopApi, apiBase]) => {
        setDripEnabled(enabled === "true");
        setDripShopId(shopId ?? "");
        setDripShopApi(shopApi ?? "");
        setDripApiBase(apiBase ?? "https://app.buildagangsheet.com");
      });
    }
  }, [activeTab, isStaff]);

  const handleSaveAnnouncement = async () => {
    setSavingAnnouncement(true);
    try {
      await updateAppSetting("trade_pricing_announcement", pricingAnnouncement);
      toast.success("Pricing announcement updated");
    } catch (error) {
      toast.error("Failed to update pricing announcement");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const handleSaveVat = async () => {
    setSavingVat(true);
    try {
      await Promise.all([
        updateAppSetting("vat_enabled", vatEnabled ? "true" : "false"),
        updateAppSetting("vat_rate", vatRate),
        updateAppSetting("vat_label", vatLabel || "VAT"),
      ]);
      toast.success("Tax settings updated");
    } catch (e) {
      toast.error((e as Error).message ?? "Failed to update tax settings");
    } finally {
      setSavingVat(false);
    }
  };

  const handleSaveIntegrations = async () => {
    setSavingIntegrations(true);
    try {
      await Promise.all([
        updateIntegration("drip_enabled", dripEnabled.toString()),
        updateIntegration("drip_shop_id", dripShopId),
        updateIntegration("drip_shop_api", dripShopApi),
        updateIntegration("drip_api_base", dripApiBase),
      ]);
      toast.success("Integrations updated");
    } catch (error) {
      toast.error("Failed to update integrations");
    } finally {
      setSavingIntegrations(false);
    }
  };

  const handleAddProduct = async () => {
    // Validation
    if (!newProduct.code.trim()) {
      toast.error("Internal code is required");
      return;
    }
    if (newProduct.price_per_m < 0) {
      toast.error("Price per metre must be at least 0");
      return;
    }
    try {
      await createProduct({
        ...newProduct,
        price_per_m: String(newProduct.price_per_m),
        uses_external_builder: newProduct.uses_external_builder,
        builder_size_id: newProduct.builder_size_id ? parseInt(newProduct.builder_size_id) : null,
      });
      toast.success("Product created");
      setShowAddProduct(false);
      setNewProduct({
        code: "",
        name: "",
        description: "",
        price_per_m: 0,
        uses_external_builder: false,
        builder_size_id: "",
      });
      // Reload products
    } catch (error) {
      toast.error("Failed to create product");
    }
  };

  const handleToggleProduct = async (id: string, active: boolean) => {
    try {
      await toggleProductActive(id, active);
      toast.success(active ? "Product enabled" : "Product disabled");
      // Reload products
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleAddShipping = async () => {
    try {
      await createShippingMethod({
        ...newShipping,
        price: parseFloat(newShipping.price),
      });
      toast.success("Shipping method created");
      setShowAddShipping(false);
      setNewShipping({ code: "", name: "", description: "", price: "" });
      // Reload shipping methods
    } catch (error) {
      toast.error("Failed to create shipping method");
    }
  };

  const handleToggleShipping = async (id: string, active: boolean) => {
    try {
      await toggleShippingActive(id, active);
      toast.success(active ? "Shipping method enabled" : "Shipping method disabled");
      // Reload shipping methods
    } catch (error) {
      toast.error("Failed to update shipping method");
    }
  };

  const handleAddCutting = async () => {
    try {
      await createCuttingTier({
        ...newCutting,
        max_items: newCutting.max_items ? parseInt(newCutting.max_items) : undefined,
        price_per_m: parseFloat(newCutting.price_per_m),
      });
      toast.success("Cutting tier created");
      setShowAddCutting(false);
      setNewCutting({
        code: "",
        name: "",
        min_items: 0,
        max_items: "",
        price_per_m: "",
        description: "",
      });
      // Reload cutting tiers
    } catch (error) {
      toast.error("Failed to create cutting tier");
    }
  };

  const handleToggleCutting = async (id: string, active: boolean) => {
    try {
      await toggleCuttingTierActive(id, active);
      toast.success(active ? "Cutting tier enabled" : "Cutting tier disabled");
      // Reload cutting tiers
    } catch (error) {
      toast.error("Failed to update cutting tier");
    }
  };

  const tabs: { id: Tab; label: string; icon: any; visible: boolean }[] = [
    { id: "account", label: "Account", icon: User, visible: true },
    {
      id: "teams",
      label: "Teams",
      icon: Users,
      visible: !isStaff && (profile?.teams_enabled ?? false),
    },
    { id: "system", label: "System & demo", icon: Sparkles, visible: isStaff },
    { id: "products-pricing", label: "Products & Pricing", icon: Package, visible: isStaff },
    { id: "integrations", label: "Integrations", icon: Zap, visible: isStaff },
    { id: "import", label: "Import Jobs", icon: Upload, visible: isAdmin },
    { id: "about", label: `About ${activeBrand.name}`, icon: Info, visible: true },
  ];

  const visibleTabs = tabs.filter((t) => t.visible);

  // Redirect non-authenticated users — must come after all hooks above
  if (!profile) {
    return <div className="text-center py-20">Please sign in to access settings.</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
      </div>

      {/* Tab navigation */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-4 overflow-x-auto md:overflow-visible">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Account tab */}
      {activeTab === "account" && (
        <div className="space-y-6">
          {/* Profile & photo */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <User className="h-5 w-5" /> Profile & photo
            </h2>
            <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-start">
              <div className="flex-shrink-0">
                <div className="relative h-24 w-24 rounded-full bg-gradient-hero flex items-center justify-center text-primary-foreground text-2xl font-bold">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt="Avatar"
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    (profile?.full_name ?? profile?.email ?? "?").slice(0, 1).toUpperCase()
                  )}
                </div>
                <Button size="sm" variant="outline" className="mt-2 w-full rounded-full" asChild>
                  <label className="cursor-pointer">
                    <Camera className="h-4 w-4 mr-2" /> Change photo
                    <input type="file" className="hidden" accept="image/*" />
                  </label>
                </Button>
              </div>
              <div className="flex-1 space-y-4">
                <div className="space-y-1.5">
                  <Label>Full name</Label>
                  <Input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="rounded-2xl"
                  />
                </div>
                <Button
                  onClick={handleSaveName}
                  disabled={savingName}
                  className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                >
                  {savingName ? "Saving..." : "Save name"}
                </Button>
              </div>
            </div>
          </div>

          {/* Email & security */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Mail className="h-5 w-5" /> Email & security
            </h2>
            <div className="mt-6 space-y-6">
              <div>
                <Label>Email</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input value={profile?.email ?? ""} readOnly className="rounded-2xl bg-muted" />
                  {!showEmailChange && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => setShowEmailChange(true)}
                    >
                      Change email
                    </Button>
                  )}
                </div>
                {showEmailChange && (
                  <div className="mt-4 space-y-3 rounded-2xl border border-border p-4 bg-muted/30">
                    <Input
                      type="email"
                      placeholder="Enter new email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="rounded-2xl"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleChangeEmail}
                        className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                      >
                        Send confirmation
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowEmailChange(false)}
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Password</Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Change your password to keep your account secure.
                  </p>
                </div>
                <Button variant="outline" onClick={handlePasswordReset} className="rounded-full">
                  <Key className="h-4 w-4 mr-2" /> Request password reset
                </Button>
              </div>
            </div>
          </div>

          {/* 2FA */}
          <MfaSection />

          {/* Appearance */}
          <AppearanceSection />

          {/* Notification preferences */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Bell className="h-5 w-5" /> Notification preferences
            </h2>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                {isStaff
                  ? "Manage how you receive notifications about new jobs and system updates."
                  : "Manage how you receive notifications about your jobs and account updates."}
              </p>
              <div className="rounded-2xl border border-border bg-muted/30 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-4 py-2 text-left font-medium">Event</th>
                      <th className="px-4 py-2 text-center font-medium">In-app</th>
                      <th className="px-4 py-2 text-center font-medium">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-4 py-3">
                        <div className="font-medium">Job created</div>
                        <div className="text-xs text-muted-foreground">
                          {isStaff ? "When new jobs are submitted" : "When your job is submitted"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={inappJobCreated} onCheckedChange={setInappJobCreated} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={emailJobCreated} onCheckedChange={setEmailJobCreated} />
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-3">
                        <div className="font-medium">Print started</div>
                        <div className="text-xs text-muted-foreground">When production begins</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={inappJobPrintStarted}
                          onCheckedChange={setInappJobPrintStarted}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={emailJobPrintStarted}
                          onCheckedChange={setEmailJobPrintStarted}
                        />
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-3">
                        <div className="font-medium">Ready for collection</div>
                        <div className="text-xs text-muted-foreground">When a job is complete</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={inappJobReady} onCheckedChange={setInappJobReady} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={emailJobReady} onCheckedChange={setEmailJobReady} />
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">
                        <div className="font-medium">Job on hold</div>
                        <div className="text-xs text-muted-foreground">
                          When a job needs attention
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={inappJobOnHold} onCheckedChange={setInappJobOnHold} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={emailJobOnHold} onCheckedChange={setEmailJobOnHold} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <Button
                onClick={handleSaveNotificationPrefs}
                disabled={savingPrefs}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                {savingPrefs ? "Saving..." : "Save preferences"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Teams tab (customers only) */}
      {activeTab === "teams" && (
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm">
            <Building className="h-4 w-4" />
            Team domain: <span className="font-semibold">{domain}</span>
          </div>

          <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 text-sm">
            <p className="text-amber-800 dark:text-amber-200">
              Teams lets you invite colleagues from your company so they can upload artwork and
              track jobs.
            </p>
            <p className="mt-2 text-amber-800 dark:text-amber-200">
              This is enabled for your account by the Resolute team.
            </p>
          </div>

          {!team ? (
            <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-soft text-primary">
                  <Users className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold">Create your team</h2>
                  <p className="mt-2 text-muted-foreground">
                    You're the first member of this team. Invite others using their @{domain} email.
                  </p>
                  <Button
                    onClick={handleCreateTeam}
                    className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                  >
                    Create team for {domain}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Plus className="h-5 w-5" /> Invite teammate
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Invite teammates using their @{domain} email.
                </p>
                <div className="mt-4 flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="invite-email" className="sr-only">
                      Email
                    </Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder={`colleague@${domain}`}
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="rounded-2xl"
                    />
                  </div>
                  <Button
                    onClick={handleInviteMember}
                    className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                  >
                    <MailIcon className="h-4 w-4 mr-2" /> Invite
                  </Button>
                </div>
              </div>

              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" /> Team members
                </h2>
                <div className="mt-4 space-y-3">
                  {members.map((member, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-2xl border border-border bg-muted/30 p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-hero text-primary-foreground text-sm font-bold">
                          {(member.full_name ?? member.email ?? "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{member.full_name ?? "Unknown"}</div>
                          <div className="text-sm text-muted-foreground">{member.email}</div>
                        </div>
                      </div>
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary capitalize">
                        {member.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* System & demo tab (staff only) */}
      {activeTab === "system" && (
        <div className="space-y-6">
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-5 w-5" /> Demo Mode
            </h2>
            <div className="mt-6 space-y-4 rounded-3xl bg-gradient-soft p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="h-4 w-4 text-accent" /> Demo Mode
                </div>
                <Switch checked={demoMode} onCheckedChange={setDemoMode} />
              </div>
              <p className="text-xs text-muted-foreground">
                {demoMode
                  ? "Showing sample data — changes won't be saved."
                  : "Connected to your live workspace."}
              </p>
            </div>
          </div>

          {/* System Health - Admin Only */}
          {isAdmin && (
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5" /> System health
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Quick health check for key system integrations and services.
              </p>
              <div className="mt-6 space-y-4">
                {/* Drip */}
                <div className="rounded-2xl border border-border bg-gradient-soft p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Drip gang sheet builder</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {systemHealth?.drip.shopIdPresent
                          ? "Shop ID present: Yes"
                          : "Shop ID present: No"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {systemHealth?.drip.status === "configured" && (
                        <div className="flex items-center gap-1.5 text-sm text-green-600">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="font-medium">Configured</span>
                        </div>
                      )}
                      {systemHealth?.drip.status === "not_configured" && (
                        <div className="flex items-center gap-1.5 text-sm text-amber-600">
                          <AlertCircle className="h-4 w-4" />
                          <span className="font-medium">Not configured</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SMTP */}
                <div className="rounded-2xl border border-border bg-gradient-soft p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Email / SMTP</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {systemHealth?.smtp.hostPresent
                          ? "SMTP host: Configured"
                          : "SMTP host: Missing"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {systemHealth?.smtp.status === "configured" && (
                        <div className="flex items-center gap-1.5 text-sm text-green-600">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="font-medium">Enabled and configured</span>
                        </div>
                      )}
                      {systemHealth?.smtp.status === "disabled" && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <XCircle className="h-4 w-4" />
                          <span className="font-medium">Disabled</span>
                        </div>
                      )}
                      {systemHealth?.smtp.status === "needs_attention" && (
                        <div className="flex items-center gap-1.5 text-sm text-amber-600">
                          <AlertCircle className="h-4 w-4" />
                          <span className="font-medium">Needs attention</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notifications */}
                <div className="rounded-2xl border border-border bg-gradient-soft p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Notifications</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {systemHealth?.notifications.jobCreatedTemplate &&
                        systemHealth?.notifications.jobStatusTemplate
                          ? "Templates: Job created, Job status"
                          : "Templates: Missing"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {systemHealth?.notifications.status === "configured" && (
                        <div className="flex items-center gap-1.5 text-sm text-green-600">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="font-medium">Templates configured</span>
                        </div>
                      )}
                      {systemHealth?.notifications.status === "missing_templates" && (
                        <div className="flex items-center gap-1.5 text-sm text-amber-600">
                          <AlertCircle className="h-4 w-4" />
                          <span className="font-medium">Missing templates</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Email & SMTP Settings - Admin Only */}
          {isAdmin && (
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MailIcon className="h-5 w-5" /> Email & SMTP Settings
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Configure email delivery settings for sending notifications to customers and staff.
              </p>
              <div className="mt-6 space-y-6">
                {/* Sender details */}
                <div className="space-y-4 rounded-3xl bg-gradient-soft p-6">
                  <h3 className="font-semibold">Sender details</h3>
                  <div>
                    <Label>From name</Label>
                    <Input
                      type="text"
                      value={fromName}
                      onChange={(e) => setFromName(e.target.value)}
                      placeholder="FlightDeck"
                      className="mt-1.5 rounded-2xl"
                    />
                  </div>
                  <div>
                    <Label>From email</Label>
                    <Input
                      type="email"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                      placeholder="noreply@flightdeck.com"
                      className="mt-1.5 rounded-2xl"
                    />
                  </div>
                </div>

                {/* SMTP configuration */}
                <div className="space-y-4 rounded-3xl bg-gradient-soft p-6">
                  <h3 className="font-semibold">SMTP configuration</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <MailIcon className="h-4 w-4 text-accent" /> Use custom SMTP
                    </div>
                    <Switch checked={smtpEnabled} onCheckedChange={setSmtpEnabled} />
                  </div>
                  {smtpEnabled && (
                    <>
                      <div>
                        <Label>Host</Label>
                        <Input
                          type="text"
                          value={smtpHost}
                          onChange={(e) => setSmtpHost(e.target.value)}
                          placeholder="smtp.example.com"
                          className="mt-1.5 rounded-2xl"
                        />
                      </div>
                      <div>
                        <Label>Port</Label>
                        <Input
                          type="text"
                          value={smtpPort}
                          onChange={(e) => setSmtpPort(e.target.value)}
                          placeholder="587"
                          className="mt-1.5 rounded-2xl"
                        />
                      </div>
                      <div>
                        <Label>Username</Label>
                        <Input
                          type="text"
                          value={smtpUsername}
                          onChange={(e) => setSmtpUsername(e.target.value)}
                          placeholder="username"
                          className="mt-1.5 rounded-2xl"
                        />
                      </div>
                      <div>
                        <Label>Password</Label>
                        <Input
                          type="password"
                          value={smtpPassword}
                          onChange={(e) => setSmtpPassword(e.target.value)}
                          placeholder="••••••••"
                          className="mt-1.5 rounded-2xl"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Password is not shown for security
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Use TLS</Label>
                          <p className="text-xs text-muted-foreground">
                            Enable secure TLS connection
                          </p>
                        </div>
                        <Switch checked={smtpUseTls} onCheckedChange={setSmtpUseTls} />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveSmtpSettings}
                    disabled={savingSmtp}
                    className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                  >
                    {savingSmtp ? "Saving..." : "Save settings"}
                  </Button>
                  <Button variant="outline" onClick={handleSendTestEmail} className="rounded-full">
                    <MailIcon className="h-4 w-4 mr-2" /> Send test email
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Email Templates - Admin Only */}
          {isAdmin && (
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Megaphone className="h-5 w-5" /> Email Templates (MVP)
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Customize email templates for job notifications. Available variables: {"{{job_id}}"}
                , {"{{job_title}}"}, {"{{customer_name}}"}, {"{{status}}"}
              </p>
              <div className="mt-6 space-y-6">
                {/* Job created template */}
                <div className="space-y-4 rounded-3xl bg-gradient-soft p-6">
                  <h3 className="font-semibold">Job created (customer)</h3>
                  <div>
                    <Label>Subject</Label>
                    <Input
                      type="text"
                      value={jobCreatedSubject}
                      onChange={(e) => setJobCreatedSubject(e.target.value)}
                      placeholder="Your job {{job_id}} has been created"
                      className="mt-1.5 rounded-2xl"
                    />
                  </div>
                  <div>
                    <Label>Body</Label>
                    <p className="text-xs text-muted-foreground mb-1.5">
                      Use {"{{job_id}}"}, {"{{job_title}}"}, {"{{customer_name}}"} as variables
                    </p>
                    <textarea
                      value={jobCreatedBody}
                      onChange={(e) => setJobCreatedBody(e.target.value)}
                      placeholder="Hi {{customer_name}},..."
                      className="mt-1.5 min-h-[120px] w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* Job status changed template */}
                <div className="space-y-4 rounded-3xl bg-gradient-soft p-6">
                  <h3 className="font-semibold">Job status changed (customer)</h3>
                  <div>
                    <Label>Subject</Label>
                    <Input
                      type="text"
                      value={jobStatusSubject}
                      onChange={(e) => setJobStatusSubject(e.target.value)}
                      placeholder="Your job {{job_id}} status update"
                      className="mt-1.5 rounded-2xl"
                    />
                  </div>
                  <div>
                    <Label>Body</Label>
                    <p className="text-xs text-muted-foreground mb-1.5">
                      Use {"{{job_id}}"}, {"{{job_title}}"}, {"{{customer_name}}"}, {"{{status}}"}{" "}
                      as variables
                    </p>
                    <textarea
                      value={jobStatusBody}
                      onChange={(e) => setJobStatusBody(e.target.value)}
                      placeholder="Hi {{customer_name}},..."
                      className="mt-1.5 min-h-[120px] w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveEmailTemplates}
                  disabled={savingTemplates}
                  className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                >
                  {savingTemplates ? "Saving..." : "Save templates"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Integrations tab (staff) */}
      {activeTab === "integrations" && isStaff && (
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5" /> Integrations
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Configure external service integrations like the DripApps Gang Sheet Builder.
          </p>
          <div className="mt-6 space-y-6">
            <div className="space-y-4 rounded-3xl bg-gradient-soft p-6">
              <h3 className="font-semibold">DripApps Gang Sheet Builder</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Zap className="h-4 w-4 text-accent" /> Enable Drip Builder
                </div>
                <Switch checked={dripEnabled} onCheckedChange={setDripEnabled} />
              </div>
              <div>
                <Label>DRIP_SHOP_ID (client-side)</Label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Used to open the gang sheet builder in the browser
                </p>
                <Input
                  type="text"
                  value={dripShopId}
                  onChange={(e) => setDripShopId(e.target.value)}
                  placeholder="Enter your Drip shop ID"
                  className="mt-1.5 rounded-2xl"
                />
              </div>
              <div>
                <Label>DRIP_SHOP_API (server-side)</Label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Reserved for future server-side API calls
                </p>
                <Input
                  type="password"
                  value={dripShopApi}
                  onChange={(e) => setDripShopApi(e.target.value)}
                  placeholder="Enter your Drip API token (optional)"
                  className="mt-1.5 rounded-2xl"
                />
              </div>
              <div>
                <Label>API base URL</Label>
                <Input
                  type="text"
                  value={dripApiBase}
                  onChange={(e) => setDripApiBase(e.target.value)}
                  placeholder="https://app.buildagangsheet.com"
                  className="mt-1.5 rounded-2xl"
                />
              </div>
              <Button
                onClick={handleSaveIntegrations}
                disabled={savingIntegrations}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                {savingIntegrations ? "Saving..." : "Save integrations"}
              </Button>

              <div className="rounded-2xl border border-border bg-background/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">Builder status</div>
                    <div className="mt-0.5 text-xs text-muted-foreground" aria-live="polite">
                      {builderHook.status === "checking" && "Checking configuration…"}
                      {builderHook.status === "disabled" &&
                        "Disabled — toggle Enable Drip Builder above and save."}
                      {builderHook.status === "missing-shop" &&
                        "Missing DRIP_SHOP_ID — set it above and save."}
                      {builderHook.status === "loading" && "Loading builder script…"}
                      {builderHook.status === "error" &&
                        (builderHook.error ?? "Failed to load builder.")}
                      {builderHook.status === "ready" && "Builder script loaded and ready."}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium",
                      builderHook.status === "ready" &&
                        "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
                      builderHook.status === "loading" &&
                        "bg-blue-500/15 text-blue-700 dark:text-blue-300",
                      builderHook.status === "checking" && "bg-muted text-muted-foreground",
                      (builderHook.status === "error" ||
                        builderHook.status === "disabled" ||
                        builderHook.status === "missing-shop") &&
                        "bg-amber-500/15 text-amber-700 dark:text-amber-300",
                    )}
                  >
                    {builderHook.status}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => builderHook.openBuilder({})}
                  disabled={builderHook.status !== "ready"}
                  className="mt-3 rounded-full"
                >
                  <Zap className="mr-1.5 h-3.5 w-3.5" /> Open builder (test)
                </Button>
              </div>
            </div>
            <div className="space-y-4 rounded-3xl bg-gradient-soft p-6">
              <h3 className="font-semibold">Builder Behaviour</h3>
              <p className="text-xs text-muted-foreground">
                Configure builder settings for staff visibility and customer guidance
              </p>
              <div>
                <Label>Supported file types</Label>
                <Input
                  type="text"
                  value={supportedFileTypes}
                  onChange={(e) => setSupportedFileTypes(e.target.value)}
                  placeholder="e.g. PNG, PDF, AI"
                  className="mt-1.5 rounded-2xl"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto Resize to 300 DPI</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically resize images to 300 DPI
                  </p>
                </div>
                <Switch checked={autoResizeDpi} onCheckedChange={setAutoResizeDpi} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Background warning</Label>
                  <p className="text-xs text-muted-foreground">
                    Warn about transparent backgrounds
                  </p>
                </div>
                <Switch checked={backgroundWarning} onCheckedChange={setBackgroundWarning} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto build</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically build gang sheet on upload
                  </p>
                </div>
                <Switch checked={autoBuild} onCheckedChange={setAutoBuild} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Flipping over image/text</Label>
                  <p className="text-xs text-muted-foreground">
                    Allow flipping elements in builder
                  </p>
                </div>
                <Switch checked={flippingEnabled} onCheckedChange={setFlippingEnabled} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Canva</Label>
                  <p className="text-xs text-muted-foreground">Allow Canva integration</p>
                </div>
                <Switch checked={canvaEnabled} onCheckedChange={setCanvaEnabled} />
              </div>
              <div>
                <Label>Builder notes</Label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Additional notes for staff (e.g., "ZIP uploads are not allowed")
                </p>
                <textarea
                  value={builderNotes}
                  onChange={(e) => setBuilderNotes(e.target.value)}
                  placeholder="Enter any additional notes..."
                  className="mt-1.5 w-full rounded-2xl border border-input bg-background px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
              <Button
                onClick={handleSaveIntegrations}
                disabled={savingIntegrations}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                {savingIntegrations ? "Saving..." : "Save builder settings"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Products & Pricing tab (staff only) */}
      {activeTab === "products-pricing" && (
        <div className="space-y-8">
          {/* Pricing announcement */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Megaphone className="h-5 w-5" /> Pricing announcement
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Shown on the pricing section and new job wizard for customers.
            </p>
            <div className="mt-4">
              <Label>Announcement text</Label>
              <textarea
                value={pricingAnnouncement}
                onChange={(e) => setPricingAnnouncement(e.target.value)}
                className="mt-1.5 min-h-[100px] w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm"
                placeholder="Enter pricing announcement..."
              />
              <Button
                onClick={handleSaveAnnouncement}
                disabled={savingAnnouncement}
                className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                {savingAnnouncement ? "Saving..." : "Save announcement"}
              </Button>
            </div>
          </div>

          {/* Tax / VAT */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Megaphone className="h-5 w-5" /> Tax / VAT
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Default tax rate applied to orders. Individual products can override this in the
              product editor.
            </p>
            <div className="mt-4 flex items-center justify-between gap-4">
              <div>
                <Label>Apply tax to orders</Label>
                <p className="text-xs text-muted-foreground">
                  When off, no tax line is shown at checkout.
                </p>
              </div>
              <Switch checked={vatEnabled} onCheckedChange={setVatEnabled} />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Tax label</Label>
                <Input
                  className="mt-1.5 rounded-2xl"
                  value={vatLabel}
                  onChange={(e) => setVatLabel(e.target.value)}
                  placeholder="VAT"
                />
              </div>
              <div>
                <Label>Default rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="mt-1.5 rounded-2xl"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  placeholder="20"
                />
              </div>
            </div>
            <Button
              onClick={handleSaveVat}
              disabled={savingVat}
              className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
            >
              {savingVat ? "Saving..." : "Save tax settings"}
            </Button>
          </div>

          {/* Products — managed in dedicated page */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5" /> Products
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add, edit, and publish products from the dedicated Products workspace.
                </p>
              </div>
              <Link
                to="/app/staff/products"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-hero px-4 py-2 text-sm font-medium text-primary-foreground shadow-soft hover:opacity-90"
              >
                <Package className="h-4 w-4" /> Open Products
              </Link>
            </div>
          </div>

          {/* Shipping methods */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Truck className="h-5 w-5" /> Shipping methods
              </h2>
              <Button
                onClick={() => setShowAddShipping(true)}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                <Plus className="h-4 w-4 mr-2" /> Add shipping
              </Button>
            </div>
            <ShippingTable onToggle={handleToggleShipping} />
          </div>

          {/* Add shipping dialog */}
          {showAddShipping && (
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h3 className="text-lg font-semibold">Add new shipping method</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label>Code (internal, unique)</Label>
                  <Input
                    value={newShipping.code}
                    onChange={(e) => setNewShipping({ ...newShipping, code: e.target.value })}
                    placeholder="e.g. standard_1_2_day"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newShipping.name}
                    onChange={(e) => setNewShipping({ ...newShipping, name: e.target.value })}
                    placeholder="e.g. Standard 1–2 day shipping"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <textarea
                    value={newShipping.description}
                    onChange={(e) =>
                      setNewShipping({ ...newShipping, description: e.target.value })
                    }
                    placeholder="Description"
                    className="mt-1.5 min-h-[80px] w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm"
                  />
                </div>
                <div>
                  <Label>Price (£)</Label>
                  <Input
                    type="text"
                    value={newShipping.price}
                    onChange={(e) => setNewShipping({ ...newShipping, price: e.target.value })}
                    placeholder="e.g. 10.50"
                    className="rounded-2xl"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddShipping}
                    className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                  >
                    Create shipping method
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddShipping(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Cutting tiers */}
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Scissors className="h-5 w-5" /> Cutting tiers
              </h2>
              <Button
                onClick={() => setShowAddCutting(true)}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                <Plus className="h-4 w-4 mr-2" /> Add cutting tier
              </Button>
            </div>
            <CuttingTiersTable onToggle={handleToggleCutting} />
          </div>

          {/* Add cutting tier dialog */}
          {showAddCutting && (
            <div className="mt-6 rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h3 className="text-lg font-semibold">Add new cutting tier</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label>Code (internal, unique)</Label>
                  <Input
                    value={newCutting.code}
                    onChange={(e) => setNewCutting({ ...newCutting, code: e.target.value })}
                    placeholder="e.g. cut_0_20"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newCutting.name}
                    onChange={(e) => setNewCutting({ ...newCutting, name: e.target.value })}
                    placeholder="e.g. Up to 20 items per metre"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Min items</Label>
                  <Input
                    type="number"
                    min={0}
                    value={newCutting.min_items}
                    onChange={(e) =>
                      setNewCutting({ ...newCutting, min_items: parseInt(e.target.value) || 0 })
                    }
                    placeholder="0"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Max items (optional)</Label>
                  <Input
                    type="number"
                    value={newCutting.max_items}
                    onChange={(e) => setNewCutting({ ...newCutting, max_items: e.target.value })}
                    placeholder="Leave empty for no upper limit"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Price per metre (£)</Label>
                  <Input
                    type="text"
                    value={newCutting.price_per_m}
                    onChange={(e) => setNewCutting({ ...newCutting, price_per_m: e.target.value })}
                    placeholder="e.g. 2.00"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <textarea
                    value={newCutting.description}
                    onChange={(e) => setNewCutting({ ...newCutting, description: e.target.value })}
                    placeholder="Description"
                    className="mt-1.5 min-h-[80px] w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddCutting}
                    className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                  >
                    Create cutting tier
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddCutting(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import tab */}
      {activeTab === "import" && <ImportJobsTab />}

      {/* About tab */}
      {activeTab === "about" && (
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Info className="h-5 w-5" /> About {activeBrand.name}
          </h2>
          <div className="mt-6 space-y-6">
            <div className="rounded-3xl bg-gradient-soft p-6">
              <h3 className="font-semibold text-lg">{activeBrand.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                A modern print production management platform designed to streamline job tracking,
                artwork review, and customer communication.
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Version:</span> 1.0.0
                </div>
                <div>
                  <span className="font-medium">Environment:</span>{" "}
                  {import.meta.env.MODE === "production" ? "Production" : "Development"}
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-gradient-soft p-6">
              <h3 className="font-semibold">Support</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Need help? Visit our Help Center for guides, tutorials, and troubleshooting.
              </p>
              <Link to="/help">
                <Button variant="outline" className="mt-4 rounded-full">
                  <Info className="h-4 w-4 mr-2" /> Visit Help Center
                </Button>
              </Link>
            </div>

            <div className="rounded-3xl bg-gradient-soft p-6">
              <h3 className="font-semibold">Privacy & Security</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Your data is securely stored and encrypted. {activeBrand.name} uses
                industry-standard security practices to protect your information.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductsTable({ onToggle }: { onToggle: (id: string, active: boolean) => Promise<void> }) {
  const qc = useQueryClient();
  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products"],
    queryFn: listProducts,
  });
  const [editing, setEditing] = useState<Product | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (p: Product) => {
    setEditing(p);
    setEditName(p.name);
    setEditDesc(p.description || "");
    setEditPrice(String(p.price_per_m));
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateProduct(editing.id, {
        name: editName,
        description: editDesc,
        price_per_m: editPrice,
      });
      toast.success("Product updated");
      qc.invalidateQueries({ queryKey: ["products"] });
      setEditing(null);
    } catch {
      toast.error("Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete product "${name}"? This cannot be undone.`)) return;
    try {
      await deleteProduct(id);
      toast.success("Product deleted");
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch {
      toast.error("Failed to delete product");
    }
  };

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading products…</div>;
  if (error) return <div className="text-sm text-red-600">Failed to load products</div>;

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 font-medium">Name</th>
            <th className="text-left py-3 px-4 font-medium">Code</th>
            <th className="text-left py-3 px-4 font-medium">Price/m (£)</th>
            <th className="text-left py-3 px-4 font-medium">Description</th>
            <th className="text-left py-3 px-4 font-medium">Active</th>
            <th className="text-left py-3 px-4 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) =>
            editing?.id === product.id ? (
              <tr key={product.id} className="border-b border-border bg-muted/30">
                <td className="py-2 px-4" colSpan={4}>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Name"
                      className="h-8 w-40 rounded-xl text-xs"
                    />
                    <Input
                      value={editPrice}
                      onChange={(e) => setEditPrice(e.target.value)}
                      placeholder="Price/m"
                      className="h-8 w-24 rounded-xl text-xs"
                    />
                    <Input
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      placeholder="Description"
                      className="h-8 w-48 rounded-xl text-xs"
                    />
                  </div>
                </td>
                <td className="py-2 px-4">
                  <Switch
                    checked={product.active}
                    onCheckedChange={(checked) => onToggle(product.id, checked)}
                  />
                </td>
                <td className="py-2 px-4">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-7 rounded-full text-xs"
                      onClick={handleSaveEdit}
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 rounded-full text-xs"
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={product.id} className="border-b border-border">
                <td className="py-3 px-4 font-medium">{product.name}</td>
                <td className="py-3 px-4 text-muted-foreground">{product.code}</td>
                <td className="py-3 px-4">
                  £{parseFloat(String(product.price_per_m) || "0").toFixed(2)}
                </td>
                <td className="py-3 px-4 text-muted-foreground">{product.description || "—"}</td>
                <td className="py-3 px-4">
                  <Switch
                    checked={product.active}
                    onCheckedChange={(checked) => onToggle(product.id, checked)}
                  />
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => startEdit(product)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(product.id, product.name)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function ShippingTable({ onToggle }: { onToggle: (id: string, active: boolean) => Promise<void> }) {
  const {
    data: shipping = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["shipping"],
    queryFn: listShippingMethods,
  });

  if (isLoading)
    return <div className="text-sm text-muted-foreground">Loading shipping methods…</div>;
  if (error) return <div className="text-sm text-red-600">Failed to load shipping methods</div>;

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 font-medium">Name</th>
            <th className="text-left py-3 px-4 font-medium">Price</th>
            <th className="text-left py-3 px-4 font-medium">Description</th>
            <th className="text-left py-3 px-4 font-medium">Active</th>
          </tr>
        </thead>
        <tbody>
          {shipping.map((method) => (
            <tr key={method.id} className="border-b border-border">
              <td className="py-3 px-4 font-medium">{method.name}</td>
              <td className="py-3 px-4">£{method.price.toFixed(2)}</td>
              <td className="py-3 px-4 text-muted-foreground">{method.description || "—"}</td>
              <td className="py-3 px-4">
                <Switch
                  checked={method.active}
                  onCheckedChange={(checked) => onToggle(method.id, checked)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CuttingTiersTable({
  onToggle,
}: {
  onToggle: (id: string, active: boolean) => Promise<void>;
}) {
  const {
    data: cuttingTiers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading cutting tiers…</div>;
  if (error) return <div className="text-sm text-red-600">Failed to load cutting tiers</div>;

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 font-medium">Name</th>
            <th className="text-left py-3 px-4 font-medium">Range</th>
            <th className="text-left py-3 px-4 font-medium">Price/m</th>
            <th className="text-left py-3 px-4 font-medium">Description</th>
            <th className="text-left py-3 px-4 font-medium">Active</th>
          </tr>
        </thead>
        <tbody>
          {cuttingTiers.map((tier) => (
            <tr key={tier.id} className="border-b border-border">
              <td className="py-3 px-4 font-medium">{tier.name}</td>
              <td className="py-3 px-4">
                {tier.max_items ? `${tier.min_items}–${tier.max_items}` : `${tier.min_items}+`}
              </td>
              <td className="py-3 px-4">
                £
                {typeof tier.price_per_m === "number"
                  ? tier.price_per_m.toFixed(2)
                  : parseFloat(tier.price_per_m || "0").toFixed(2)}
              </td>
              <td className="py-3 px-4 text-muted-foreground">{tier.description || "—"}</td>
              <td className="py-3 px-4">
                <Switch
                  checked={tier.active}
                  onCheckedChange={(checked) => onToggle(tier.id, checked)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --------------- MFA TOTP Section ---------------
function MfaSection() {
  type MfaStep = "idle" | "enrolling" | "verifying" | "enabled";
  const [step, setStep] = useState<MfaStep>("idle");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [factorId, setFactorId] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = data?.totp?.find((f) => f.status === "verified");
      if (verified) {
        setFactorId(verified.id);
        setStep("enabled");
      }
      setChecking(false);
    };
    check();
  }, []);

  const handleEnroll = async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
    setLoading(false);
    if (error || !data) {
      toast.error(error?.message ?? "Enrollment failed");
      return;
    }
    setFactorId(data.id);
    setQrCode(data.totp.qr_code);
    setSecret(data.totp.secret);
    setStep("enrolling");
  };

  const handleVerify = async () => {
    if (verifyCode.length < 6) return;
    setLoading(true);
    const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({
      factorId,
    });
    if (challengeErr || !challenge) {
      toast.error(challengeErr?.message ?? "Challenge failed");
      setLoading(false);
      return;
    }
    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: verifyCode,
    });
    setLoading(false);
    if (verifyErr) {
      toast.error("Incorrect code, please try again.");
      return;
    }
    toast.success("2FA enabled successfully");
    setStep("enabled");
    setVerifyCode("");
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable 2FA? Your account will be less secure."))
      return;
    setLoading(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("2FA disabled");
    setFactorId("");
    setQrCode("");
    setSecret("");
    setStep("idle");
  };

  if (checking) {
    return (
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft animate-pulse">
        <div className="h-5 w-48 rounded bg-muted" />
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Shield className="h-5 w-5" /> Two-factor authentication
      </h2>

      {step === "idle" && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Add an extra layer of security to your account with a TOTP authenticator app (e.g.
            Google Authenticator, Authy).
          </p>
          <Button
            onClick={handleEnroll}
            disabled={loading}
            className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            {loading ? "Loading…" : "Set up 2FA"}
          </Button>
        </div>
      )}

      {step === "enrolling" && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
          </p>
          {qrCode && (
            <div className="flex justify-center rounded-2xl border border-border bg-white p-4">
              <img src={qrCode} alt="TOTP QR code" className="h-40 w-40" />
            </div>
          )}
          <div className="rounded-2xl border border-border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground mb-1">Manual entry secret</p>
            <code className="break-all text-xs font-mono">{secret}</code>
          </div>
          <div className="space-y-1.5">
            <Label>Verification code</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
              className="rounded-2xl text-center tracking-widest text-lg"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleVerify}
              disabled={loading || verifyCode.length < 6}
              className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
            >
              {loading ? "Verifying…" : "Enable 2FA"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setStep("idle");
                setQrCode("");
                setSecret("");
                setVerifyCode("");
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {step === "enabled" && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-600 border border-green-500/20">
              <CheckCircle2 className="h-3 w-3" /> Enabled
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Your account is protected with two-factor authentication.
          </p>
          <Button
            variant="outline"
            onClick={handleDisable}
            disabled={loading}
            className="rounded-full text-destructive hover:bg-destructive/10"
          >
            {loading ? "Disabling…" : "Disable 2FA"}
          </Button>
        </div>
      )}
    </div>
  );
}

// --------------- Import Jobs Tab (Monday.com CSV) ---------------
function ImportJobsTab() {
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);

  const fieldOptions = [
    { value: "", label: "— Skip column —" },
    { value: "job_title", label: "Job Title *" },
    { value: "customer_name", label: "Customer / Company *" },
    { value: "status", label: "Status" },
    { value: "product_type", label: "Product Type" },
    { value: "quantity", label: "Quantity" },
    { value: "due_date", label: "Due Date" },
    { value: "notes", label: "Notes" },
  ];

  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return;
    const parseLine = (line: string): string[] => {
      const out: string[] = [];
      let cur = "";
      let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') inQ = !inQ;
        else if (c === "," && !inQ) {
          out.push(cur.trim());
          cur = "";
        } else cur += c;
      }
      out.push(cur.trim());
      return out;
    };
    const parsed = lines.map(parseLine);
    const hdrs = parsed[0].map((h) => h.replace(/^"|"$/g, ""));
    setHeaders(hdrs);
    setRows(parsed.slice(1));
    // Auto-map common Monday column names
    const auto: Record<string, string> = {};
    hdrs.forEach((h) => {
      const l = h.toLowerCase();
      if (l.includes("job") && l.includes("name")) auto[h] = "job_title";
      else if (l.includes("title") && !l.includes("status")) auto[h] = "job_title";
      else if (l.includes("company") || l.includes("customer")) auto[h] = "customer_name";
      else if (l === "status") auto[h] = "status";
      else if (l.includes("product")) auto[h] = "product_type";
      else if (l.includes("qty") || l.includes("quantity")) auto[h] = "quantity";
      else if (l.includes("due")) auto[h] = "due_date";
      else if (l === "notes" || l.includes("description")) auto[h] = "notes";
    });
    setMapping(auto);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => parseCSV(ev.target?.result as string);
    r.readAsText(f);
  };

  const handleImport = async () => {
    setErrors([]);
    setImported(0);
    const titleCol = Object.keys(mapping).find((k) => mapping[k] === "job_title");
    const customerCol = Object.keys(mapping).find((k) => mapping[k] === "customer_name");
    if (!titleCol || !customerCol) {
      toast.error("Map Job Title and Customer/Company columns first");
      return;
    }
    setImporting(true);
    const newErrors: string[] = [];
    let success = 0;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const get = (col: string | undefined) => (col ? (row[headers.indexOf(col)] ?? "") : "");
      const title = get(titleCol).trim();
      const companyName = get(customerCol).trim();
      if (!title || !companyName) {
        newErrors.push(`Row ${i + 2}: missing title or customer`);
        continue;
      }
      try {
        // Find or create customer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: existing } = await (supabase as any)
          .from("customers")
          .select("id")
          .ilike("company_name", companyName)
          .maybeSingle();
        let customerId = existing?.id;
        if (!customerId) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: created, error: cErr } = await (supabase as any)
            .from("customers")
            .insert({ company_name: companyName })
            .select("id")
            .single();
          if (cErr) throw cErr;
          customerId = created.id;
        }
        // Insert job
        const statusRaw = get(Object.keys(mapping).find((k) => mapping[k] === "status"));
        const status = [
          "New",
          "Artwork Check",
          "Gang Sheet",
          "Queued for Print",
          "Printing",
          "Curing",
          "Quality Check",
          "Ready for Dispatch",
          "Completed",
          "On Hold",
        ].includes(statusRaw)
          ? statusRaw
          : "Completed";
        const qtyRaw = get(Object.keys(mapping).find((k) => mapping[k] === "quantity"));
        const qty = parseInt(qtyRaw, 10);
        const dueRaw = get(Object.keys(mapping).find((k) => mapping[k] === "due_date"));
        const due = dueRaw && !isNaN(Date.parse(dueRaw)) ? new Date(dueRaw).toISOString() : null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: jErr } = await (supabase as any).from("jobs").insert({
          customer_id: customerId,
          job_title: `[Imported] ${title}`,
          status,
          quantity: isNaN(qty) ? 1 : qty,
          due_date: due,
          product_type:
            get(Object.keys(mapping).find((k) => mapping[k] === "product_type")) || null,
          notes:
            get(Object.keys(mapping).find((k) => mapping[k] === "notes")) ||
            "Imported from Monday.com",
          payment_status: "pending",
        });
        if (jErr) throw jErr;
        success++;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        newErrors.push(`Row ${i + 2}: ${msg}`);
      }
    }
    setImported(success);
    setErrors(newErrors);
    setImporting(false);
    if (success > 0) toast.success(`Imported ${success} job${success !== 1 ? "s" : ""}`);
    if (newErrors.length > 0)
      toast.error(`${newErrors.length} row${newErrors.length !== 1 ? "s" : ""} failed`);
  };

  const reset = () => {
    setHeaders([]);
    setRows([]);
    setMapping({});
    setErrors([]);
    setImported(0);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Upload className="h-5 w-5" /> Import jobs from Monday.com
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Upload a CSV export from Monday.com. Map each column to a FlightDeck field, then run the
          import. Imported jobs are tagged{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">[Imported]</code> and default to
          Completed status.
        </p>

        {headers.length === 0 ? (
          <div className="mt-6 rounded-3xl border-2 border-dashed border-border bg-gradient-soft p-10 text-center">
            <FileSpreadsheet className="mx-auto h-10 w-10 text-muted-foreground/60" />
            <p className="mt-3 text-sm font-medium">Upload Monday.com CSV export</p>
            <p className="mt-1 text-xs text-muted-foreground">CSV files only</p>
            <label className="mt-4 inline-flex">
              <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
              <span className="cursor-pointer rounded-full bg-gradient-hero px-5 py-2 text-sm font-medium text-primary-foreground shadow-soft hover:opacity-90">
                Choose file
              </span>
            </label>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="flex items-center justify-between rounded-2xl bg-muted/30 px-4 py-3">
              <div className="text-sm">
                <strong>{rows.length}</strong> rows · <strong>{headers.length}</strong> columns
              </div>
              <Button variant="outline" size="sm" onClick={reset} className="rounded-full">
                Choose different file
              </Button>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Map columns</h3>
              <div className="space-y-2">
                {headers.map((h) => (
                  <div key={h} className="flex items-center gap-3">
                    <div className="flex-1 truncate rounded-xl bg-muted/40 px-3 py-2 text-sm font-mono">
                      {h}
                    </div>
                    <span className="text-muted-foreground">→</span>
                    <select
                      value={mapping[h] ?? ""}
                      onChange={(e) => setMapping((prev) => ({ ...prev, [h]: e.target.value }))}
                      className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm"
                    >
                      {fieldOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleImport}
                disabled={importing}
                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
              >
                <Download className="mr-2 h-4 w-4" />
                {importing
                  ? `Importing… (${imported}/${rows.length})`
                  : `Import ${rows.length} jobs`}
              </Button>
            </div>

            {imported > 0 && (
              <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-sm">
                <CheckCircle2 className="inline h-4 w-4 mr-1.5 text-green-600" />
                Successfully imported <strong>{imported}</strong> job{imported !== 1 ? "s" : ""}.
              </div>
            )}

            {errors.length > 0 && (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
                <div className="text-sm font-medium text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" /> {errors.length} row
                  {errors.length !== 1 ? "s" : ""} failed
                </div>
                <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs text-muted-foreground">
                  {errors.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AppearanceSection() {
  const { theme, resolvedTheme } = useTheme();
  const activeBrand = useBrand();
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Sparkles className="h-5 w-5" /> Appearance
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Choose how {activeBrand.name} looks. System matches your device setting and updates
        automatically.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ThemeToggle />
        <span className="text-xs text-muted-foreground">
          Current: <span className="font-medium text-foreground">{theme}</span>
          {theme === "system" && (
            <>
              {" "}
              (resolved to <span className="font-medium text-foreground">{resolvedTheme}</span>)
            </>
          )}
        </span>
      </div>
    </div>
  );
}
